/**
 * EngageHistory — /engage/history
 * Comprehensive history of all campaigns across all channels.
 */
import React from 'react';
import { History, Loader2, MessageSquare } from 'lucide-react';
import { useCampaigns } from '../context/CampaignsContext';
import { useRole }      from '../context/RoleContext';
import SMSCampaignHistoryCard from '../components/engage/SMSCampaignHistoryCard';

const EngageHistory = () => {
  const { campaigns, loading } = useCampaigns();
  const { role } = useRole();

  // Summary stats
  const totalSent     = campaigns.reduce((sum, c) => sum + (c.sentCount || 0), 0);
  const totalFailed   = campaigns.reduce((sum, c) => sum + (c.failedCount || 0), 0);
  const totalScheduled = campaigns.filter(c => c.status === 'scheduled').length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-900">Campaign History</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          All sent and scheduled campaigns across channels
        </p>
      </div>

      {/* Aggregate stats */}
      {campaigns.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Total Campaigns" value={campaigns.length} />
          <StatCard label="SMS Messages Sent" value={totalSent.toLocaleString()} />
          <StatCard label="Scheduled" value={totalScheduled} />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
            <History className="w-7 h-7 text-gray-300" />
          </div>
          <h3 className="text-base font-semibold text-gray-800 mb-1">No campaign history yet</h3>
          <p className="text-sm text-gray-500 max-w-xs mx-auto">
            Campaigns you send will appear here with delivery stats.
          </p>
        </div>
      ) : (
        <>
          {/* SMS campaigns section */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-4 h-4 text-orange-500" />
              <h3 className="text-sm font-semibold text-gray-700">SMS Campaigns ({campaigns.length})</h3>
            </div>
            <div className="space-y-3" data-testid="history-campaign-list">
              {campaigns.map((c) => (
                <SMSCampaignHistoryCard key={c.id} campaign={c} userRole={role} />
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-xs text-gray-400 mt-0.5">{label}</p>
  </div>
);

export default EngageHistory;
